import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  getAllFees,
  createFee,
  updateFee,
  deleteFee,
  getFeeById
} from "../../../services/feeService";
import { Fee, FeeCreatePayload, PaginatedFees } from "../../../types/feeTypes";
import { getStudents } from "../../../services/studentService";
import { StudentType } from "types/studentTypes";
import { getAllFeeTypes } from "services/feeTypeService";
import { FeeType } from "types/feeTypeTypes";
import { all_routes } from "router/all_routes";
import { useNavigate } from "react-router-dom";

export const useFee = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fees, setFees] = useState<Fee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedFee, setSelectedFee] = useState<Fee | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [students, setStudents] = useState<StudentType[]>([]);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });
  const fetchFees = useCallback(async (params: { [key: string]: string | number | null } = {}) => {
    setLoading(true);
    try {
      const response = await getAllFees(params);
      setFees(response.data.data);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_fetch_fees"));
      setError(t("failed_to_fetch_fees"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCreate = async (payload: FeeCreatePayload) => {
    try {
      await createFee(payload);
      message.success(t("fee_added_successfully"));
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_create_fee"));
      return false;
    }
  };

  const handleGetFeeById = async (feeId: string) => {
    try {
      const response = await getFeeById(feeId);
      setLoading(false);
      return response.data;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_get_fee"));
      setLoading(false);
      navigate(all_routes.error404);
      return false;
    }
  };

  // const handleUpdate = async (feeId: string, payload: FeeUpdatePayload) => {
  //   try {
  //     await updateFee(feeId, payload);
  //     message.success(t("fee_updated_successfully"));
  //     await fetchFees();
  //     return true;
  //   } catch (err) {
  //     console.error(err);
  //     message.error(t("failed_to_update_fee"));
  //     return false;
  //   }
  // };

  const handleDelete = async (feeId: string) => {
    try {
      await deleteFee(feeId);
      message.success(t("fee_deleted_successfully"));
      await fetchFees();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_delete_fee"));
      return false;
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await getStudents({ page: 1, limit: 100000 });
      setStudents(response.data);
    } catch (error) {
      message.error(t("failed_to_fetch_students"));
      return false;
    }
  };

  const fetchFeeTypes = async () => {
    try {
      const response = await getAllFeeTypes({});
      setFeeTypes(response.data.data);
    } catch (error) {
      message.error(t("failed_to_fetch_fee_types"));
      return false;
    }
  };

  const statusFee = [
    { label: t("fees_status.overdue"), value: "overdue" },
    { label: t("fees_status.coming"), value: "coming" },
    { label: t("fees_status.partially_paid"), value: "partially_paid" },
    { label: t("fees_status.paid"), value: "paid" },
    { label: t("fees_status.failed"), value: "failed" }
  ]

  return {
    fees: fees,
    infoPagination: {
      total: pagination.total,
      page: pagination.page,
      limit: pagination.limit,
    },
    loading,
    error,
    selectedFee,
    setSelectedFee,
    isModalOpen,
    setIsModalOpen,
    fetchFees,
    handleCreate,
    handleDelete,
    fetchStudents,
    students,
    fetchFeeTypes,
    feeTypes,
    statusFee,
    handleGetFeeById
  };
};

export default useFee;
